import { yupResolver } from '@hookform/resolvers/yup';
import { isValidPhoneNumber } from 'libphonenumber-js/min';
import { UserCircle } from 'lucide-react';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useFeatureFlagEnabled, useFeatureFlagPayload } from 'posthog-js/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import photo from '../assets/images/profile-photo-base.png';
import Button from '../components/common/button';
import ConfirmCodex from '../components/common/confirm-codex';
import Input from '../components/common/input';
import Loader from '../components/common/loader';
import { PhoneInputWithMask } from '../components/common/phone-input-with-mask';
import Tooltip from '../components/common/tooltip';
import { UnsavedChangesModal } from '../components/common/unsaved-changes-modal';
import { ArrowTopRight } from '../components/icons/arrow-top-right';
import { baseUrl } from '../configs/api/api.config';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { useChangeAvatarMutation, useChangeUserMutation } from '../services/users.api';
import { selectUser, setUserData } from '../store/auth.slice';
import { CustomSelect } from '../components/common/select';
import { HotOfferCard } from '../components/profile/hot-offer-card';
import { CatalogProfileTab } from '../components/profile/CatalogProfileTab';
import {
  getProfileHotOfferCompletedStorageKey,
  getProfileHotOfferSnoozedUntilStorageKey,
  parseProfileHotOfferPayload,
} from '../components/profile/hot-offer-data';
import { FEATURE_FLAG_ENABLE_PROFILE_CATALOG, FEATURE_FLAG_PROFILE_HOT_OFFER } from '../constants/featureFlags';
import { useGetSpecializationsQuery } from '../services/directus.api';
/** Стили полей как в макете figma (Общая информация) */
const figmaLabelClass = 'mb-2 gap-1 text-sm font-medium text-gray-700';
const figmaRequiredHintClass = 'ml-1 text-xs font-normal text-gray-500';
const figmaInputClass =
  '!h-auto !min-h-[48px] !rounded-lg !border !border-gray-300 !px-4 !py-3 !text-gray-900 !shadow-none focus:!border-transparent focus:!outline-none focus:!ring-2 focus:!ring-red-500';
const figmaInputInnerClass = '!h-auto min-h-[48px]';
const figmaPhoneClass =
  '!h-auto !min-h-[48px] !rounded-lg !border !border-gray-300 !p-3 !shadow-none focus-within:!border-transparent focus-within:!ring-2 focus-within:!ring-red-500 [&>input]:!text-gray-900';

const schema = yup.object().shape({
  firstName: yup.string().required('Заполните поле'),
  lastName: yup.string().required('Заполните поле'),
  middleName: yup.string(),
  specialization: yup.string(),
  phone: yup
    .string()
    .required('Введите телефон')
    .test('is-valid-phone', 'Некорректный номер телефона', (value) => isValidPhoneNumber(value || '')),
  agreeToReceiveEmails: yup.boolean(),
});

type FormData = yup.InferType<typeof schema>;

type Tab = 'general' | 'catalog';

const Profile = () => {
  const userData = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('general');
  const [isCatalogTabDirty, setIsCatalogTabDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState<Tab | null>(null);
  const [isShowCodex, setIsShowCodex] = useState(false);
  const [dismissedHotOfferId, setDismissedHotOfferId] = useState<string | null>(null);
  const [avaPath, setAvaPath] = useState('');
  const [changeUser] = useChangeUserMutation();
  const [changeUserAvatar, { isLoading: isLoadingAva }] = useChangeAvatarMutation();
  const { data: specializationResponse } = useGetSpecializationsQuery();
  const hotOfferPayload = useFeatureFlagPayload(FEATURE_FLAG_PROFILE_HOT_OFFER);
  const isCatalogProfileEnabled = useFeatureFlagEnabled(FEATURE_FLAG_ENABLE_PROFILE_CATALOG);

  const { control, handleSubmit, reset, watch } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      middleName: '',
      phone: '',
      specialization: '',
      agreeToReceiveEmails: false,
    },
  });

  useEffect(() => {
    if (userData) {
      reset({
        firstName: userData.first_name,
        lastName: userData.last_name,
        middleName: userData.middle_name || '',
        specialization: userData.specialization ? String(userData.specialization) : '',
        phone: userData.phone,
        agreeToReceiveEmails: userData.agree_to_receive_emails,
      });
      setAvaPath(userData.avatar || '');
    }
  }, [userData, reset]);

  useEffect(() => {
    if (userData && !userData.agree_with_code) {
      showCodex();
    }
  }, [userData]);

  const showCodex = () => {
    setIsShowCodex(true);
    document.body.classList.add('hidden_scroll');
  };

  const onChangeAvatar = (file: ChangeEvent<HTMLInputElement>) => {
    const files = file.target.files;
    const format = files?.[0]?.name.split('.').at(-1)?.toLowerCase();
    if (format && !['jpg', 'jpeg', 'png'].includes(format)) {
      toast.warn('Загрузите изображение в формате - .jpg, .jpeg, .png');
      return;
    }
    if (!format || !files?.length) return;

    changeUserAvatar(files[0])
      .unwrap()
      .then((res) => {
        setAvaPath(res.path);
      });
  };

  const hasFormChanges = useCallback(
    (data: Partial<FormData>) => {
      if (!userData) return false;
      return (
        (data.firstName ?? '') !== (userData.first_name ?? '') ||
        (data.lastName ?? '') !== (userData.last_name ?? '') ||
        (data.middleName ?? '') !== (userData.middle_name ?? '') ||
        String(data.specialization ?? '') !== String(userData.specialization ?? '') ||
        (data.phone ?? '') !== (userData.phone ?? '') ||
        Boolean(data.agreeToReceiveEmails) !== Boolean(userData.agree_to_receive_emails)
      );
    },
    [userData],
  );

  const onSubmit = async (data: FormData) => {
    if (!userData || !hasFormChanges(data)) return;
    try {
      const newUserData = await changeUser({
        id: userData.id,
        first_name: data.firstName,
        last_name: data.lastName,
        middle_name: data.middleName,
        specialization:
          data.specialization === ''
            ? null
            : isNaN(Number(data.specialization))
              ? data.specialization
              : Number(data.specialization),
        phone: data.phone,
        agree_to_receive_emails: data.agreeToReceiveEmails,
      });
      dispatch(setUserData(newUserData?.data || userData));
      toast.success('Данные успешно сохранены');
    } catch (e) {
      console.error(e);
    }
  };

  const categories = useMemo(
    () => (specializationResponse?.data ?? []).slice().sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)),
    [specializationResponse],
  );

  const specializationTitles = useMemo(
    () => categories.map((item) => item.title).filter((title): title is string => Boolean(title)),
    [categories],
  );

  const specializationTitleById = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((item) => {
      if (item.title) map.set(String(item.id), item.title);
    });
    return map;
  }, [categories]);

  const specializationIdByTitle = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((item) => {
      if (item.title) map.set(item.title, String(item.id));
    });
    return map;
  }, [categories]);

  const watchedValues = watch();
  const isSaveButtonEnabled = hasFormChanges(watchedValues as Partial<FormData>);

  const hotOffer = useMemo(() => parseProfileHotOfferPayload(hotOfferPayload), [hotOfferPayload]);
  const isHotOfferDismissed = useMemo(() => {
    if (!hotOffer) return true;
    if (dismissedHotOfferId === hotOffer.id) return true;
    if (typeof window === 'undefined') return false;
    if (localStorage.getItem(getProfileHotOfferCompletedStorageKey(hotOffer.id)) === '1') return true;
    const snoozedUntilRaw = localStorage.getItem(getProfileHotOfferSnoozedUntilStorageKey(hotOffer.id));
    if (!snoozedUntilRaw) return false;
    const snoozedUntil = Number(snoozedUntilRaw);
    if (!Number.isFinite(snoozedUntil)) {
      localStorage.removeItem(getProfileHotOfferSnoozedUntilStorageKey(hotOffer.id));
      return false;
    }
    if (Date.now() < snoozedUntil) return true;
    localStorage.removeItem(getProfileHotOfferSnoozedUntilStorageKey(hotOffer.id));
    return false;
  }, [dismissedHotOfferId, hotOffer]);

  const dismissHotOffer = () => {
    if (!hotOffer) return;
    const snoozedUntil = Date.now() + hotOffer.showAgainAfterHours * 60 * 60 * 1000;
    localStorage.setItem(getProfileHotOfferSnoozedUntilStorageKey(hotOffer.id), String(snoozedUntil));
    setDismissedHotOfferId(hotOffer.id);
  };

  const acceptHotOffer = () => {
    if (!hotOffer) return;
    if (hotOffer.primaryLink) {
      if (hotOffer.primaryLink.startsWith('/')) {
        navigate(hotOffer.primaryLink);
      } else {
        try {
          const parsedUrl = new URL(hotOffer.primaryLink);
          const isInternal = parsedUrl.origin === window.location.origin;
          if (isInternal) {
            navigate(`${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`);
          } else {
            window.open(hotOffer.primaryLink, '_blank', 'noopener,noreferrer');
          }
        } catch {
          window.open(hotOffer.primaryLink, '_blank', 'noopener,noreferrer');
        }
      }
    } else if (hotOffer.successToast?.trim()) {
      toast.success(hotOffer.successToast.trim());
    }
    localStorage.setItem(getProfileHotOfferCompletedStorageKey(hotOffer.id), '1');
    setDismissedHotOfferId(hotOffer.id);
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'general', label: 'Общая информация' },
    ...(isCatalogProfileEnabled ? [{ id: 'catalog' as const, label: 'Профиль в каталоге' }] : []),
  ];

  useEffect(() => {
    if (!isCatalogProfileEnabled && activeTab === 'catalog') {
      setActiveTab('general');
    }
  }, [activeTab, isCatalogProfileEnabled]);

  const handleTabClick = (tab: Tab) => {
    if (tab === activeTab) return;
    if (activeTab === 'catalog' && isCatalogTabDirty) {
      setPendingTab(tab);
      return;
    }
    setActiveTab(tab);
  };

  return (
    <div className="w-full h-full pb-[36px]">
      {/* Tabs */}
      <div className="mb-6 flex gap-1 rounded-xl bg-gray-100 p-1 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleTabClick(tab.id)}
            className={`rounded-lg px-5 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'general' && (
        <div className="w-full flex flex-col xl:flex-row gap-[40px] lg:gap-[24px]">
          <div className="flex w-full max-w-[330px] flex-col items-center gap-[30px] shrink-0">
            {hotOffer && !isHotOfferDismissed && (
              <HotOfferCard
                className="w-full shrink-0"
                title={hotOffer.title}
                description={hotOffer.description}
                primaryLabel={hotOffer.primaryLabel}
                secondaryLabel={hotOffer.secondaryLabel}
                onAccept={acceptHotOffer}
                onDismiss={dismissHotOffer}
              />
            )}
            <div className="w-full h-[350px] lg:h-[320px] flex items-center justify-center rounded-[20px] overflow-hidden relative">
              {isLoadingAva ? (
                <Loader embedInParent />
              ) : (
                <img src={avaPath ? baseUrl + avaPath : photo} className="w-full h-full object-cover" alt="" />
              )}
              <div className="w-full h-full absolute t-0 l-0 hover:bg-blackWithOpacity40 flex items-center justify-center opacity-0 hover:opacity-100">
                <input
                  id="ava"
                  type="file"
                  accept=".png, .jpg, .jpeg"
                  onChange={onChangeAvatar}
                  className="w-full h-full absolute t-0 l-0 hover:cursor-pointer opacity-0"
                />
                <label
                  htmlFor="ava"
                  className="font-semibold text-white text-lg underline text-redBase relative z-10 hover:text-redBase hover:cursor-pointer"
                >
                  Изменить
                </label>
              </div>
            </div>
            <Button
              type="bordered"
              className="flex justify-between items-center gap-[10px] py-[10px] group hover:text-white!"
              onClick={showCodex}
            >
              <span className="text-base">Этический кодекс психолога</span>
              <ArrowTopRight />
            </Button>
          </div>

          <form className="flex min-w-0 flex-1 flex-col" onSubmit={handleSubmit(onSubmit)}>
            <div className="w-full rounded-2xl bg-white p-6 shadow-sm md:p-8">
              <h2 className="mb-6 text-2xl font-bold text-gray-900">Общая информация</h2>
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Input
                        classNameWrap="md:min-w-0"
                        classNameLabel={figmaLabelClass}
                        classNameRequiredHint={figmaRequiredHintClass}
                        classNameInnerWrap={figmaInputInnerClass}
                        className={figmaInputClass}
                        label="Фамилия *"
                        showRequired
                        placeholder="Введите фамилию"
                        onChange={field.onChange}
                        value={field.value || ''}
                        error={error?.message}
                        maxLength={20}
                      />
                    )}
                  />
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Input
                        classNameWrap="md:min-w-0"
                        classNameLabel={figmaLabelClass}
                        classNameRequiredHint={figmaRequiredHintClass}
                        classNameInnerWrap={figmaInputInnerClass}
                        className={figmaInputClass}
                        label="Имя *"
                        showRequired
                        placeholder="Введите имя"
                        onChange={field.onChange}
                        value={field.value || ''}
                        error={error?.message}
                        maxLength={16}
                      />
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Controller
                    name="middleName"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Input
                        classNameWrap="md:min-w-0"
                        classNameLabel={figmaLabelClass}
                        classNameInnerWrap={figmaInputInnerClass}
                        className={figmaInputClass}
                        label="Отчество"
                        placeholder="Введите отчество"
                        onChange={field.onChange}
                        value={field.value || ''}
                        error={error?.message}
                        maxLength={20}
                      />
                    )}
                  />
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <PhoneInputWithMask
                        classNameWrap="md:min-w-0"
                        classNameLabel={figmaLabelClass}
                        classNameRequiredHint={figmaRequiredHintClass}
                        classNamePhone={figmaPhoneClass}
                        label="Телефон *"
                        selected={field.value}
                        onChange={field.onChange}
                        onBlur={() => field.onBlur()}
                        error={error?.message}
                        required
                        showRequired
                      />
                    )}
                  />
                </div>

                <Controller
                  name="specialization"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <div className="w-full">
                      <span className="mb-2 block text-sm font-medium text-gray-700">Специализация</span>
                      <CustomSelect
                        className="!gap-2"
                        classNameInput={figmaInputClass}
                        classNameUlList="rounded-b-lg border-gray-300"
                        items={specializationTitles}
                        placeholder="Выберите категорию"
                        selected={specializationTitleById.get(String(field.value)) || ''}
                        onChange={(val) => field.onChange(specializationIdByTitle.get(val) ?? '')}
                        error={error?.message}
                        showError={true}
                        isChange={false}
                      />
                    </div>
                  )}
                />

                <Input
                  classNameLabel={figmaLabelClass}
                  classNameInnerWrap={figmaInputInnerClass}
                  className={`${figmaInputClass} cursor-default`}
                  classNameWrap="w-full"
                  label="Email"
                  placeholder="Введите email"
                  type="email"
                  autoComplete="email"
                  readOnly
                  value={userData?.email || ''}
                  icon={{
                    type: 'suffix',
                    icon: (
                      <Tooltip text="Для смены email надо обратиться к администратору сервиса info@zingizi.pro">
                        <span className="pointer-events-auto inline-flex items-center text-gray-400 hover:text-gray-600">
                          <UserCircle className="h-5 w-5" />
                        </span>
                      </Tooltip>
                    ),
                  }}
                />

                <Controller
                  name="agreeToReceiveEmails"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center gap-2">
                      <input
                        id="profile-newsletter"
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="h-5 w-5 rounded border-gray-300 accent-red-600 focus:ring-red-500"
                      />
                      <label htmlFor="profile-newsletter" className="text-sm text-gray-700">
                        Я согласен получать новости и рекламные рассылки
                      </label>
                    </div>
                  )}
                />

                <button
                  type="submit"
                  disabled={!isSaveButtonEnabled}
                  className="w-full rounded-lg bg-gray-400 py-4 font-medium text-white transition-colors hover:bg-gray-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Сохранить
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {isCatalogProfileEnabled && activeTab === 'catalog' && <CatalogProfileTab onDirtyChange={setIsCatalogTabDirty} />}

      {(isShowCodex || !userData?.agree_with_code) && (
        <ConfirmCodex
          closeCodex={() => {
            setIsShowCodex(false);
            document.body.classList.remove('hidden_scroll');
          }}
        />
      )}

      <UnsavedChangesModal
        isOpen={pendingTab !== null}
        onStay={() => setPendingTab(null)}
        onLeave={() => {
          if (pendingTab) setActiveTab(pendingTab);
          setIsCatalogTabDirty(false);
          setPendingTab(null);
        }}
      />
    </div>
  );
};

export default Profile;
